import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plugs, Trash, Copy, Check } from "@phosphor-icons/react";
import IntegrationsHealth from "@/components/IntegrationsHealth";

export default function Integrations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const loadApiKeys = async () => {
    if (!isAdmin) return;
    try {
      setApiKeysLoading(true);
      const r = await api.get("/api-keys");
      setApiKeys(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte hämta API-nycklar");
    } finally {
      setApiKeysLoading(false);
    }
  };

  useEffect(() => { loadApiKeys(); }, [isAdmin]);

  const createApiKey = async (e) => {
    e.preventDefault();
    const label = newKeyLabel.trim();
    if (!label) {
      toast.error("Ange en etikett, t.ex. \"kaplist-track produktion\"");
      return;
    }
    setCreatingKey(true);
    try {
      const r = await api.post("/api-keys", { label });
      setRevealedKey(r.data);
      setNewKeyLabel("");
      setCopiedKey(false);
      toast.success("API-nyckel skapad — kopiera nyckeln nu, den visas inte igen");
      await loadApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte skapa API-nyckel");
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeApiKey = async (k) => {
    if (!window.confirm(`Återkalla API-nyckeln "${k.label}"? Integrationen kommer omedelbart att förlora åtkomst.`)) return;
    try {
      await api.delete(`/api-keys/${k.id}`);
      toast.success("API-nyckel återkallad");
      await loadApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte återkalla API-nyckeln");
    }
  };

  const copyPlaintextKey = async () => {
    if (!revealedKey?.plaintext_key) return;
    try {
      await navigator.clipboard.writeText(revealedKey.plaintext_key);
      setCopiedKey(true);
      toast.success("Nyckel kopierad till urklipp");
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      toast.error("Kunde inte kopiera — markera och kopiera manuellt");
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4 max-w-2xl" data-testid="integrations-page">
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Integrationer</h1>
        <p className="text-text-muted">Endast administratörer kan hantera integrationer.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl" data-testid="integrations-page">
      <header>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Integrationer</h1>
        <p className="text-text-muted mt-1">
          Hantera API-nycklar för externa system som kopplas mot Tidsrapporteringen.
        </p>
      </header>

      <IntegrationsHealth />

      <section className="tk-card p-6" data-testid="api-keys-section">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
          <Plugs size={14} /> API-nycklar
        </div>
        <p className="text-sm text-text-muted mt-2">
          Skapa långlivade API-nycklar för externa system (t.ex.{" "}
          <span className="font-medium text-text-primary">kaplist-track</span>) som ska hämta
          och ersätta Excel-filer kopplade till objekt. Nyckeln visas i klartext{" "}
          <span className="font-medium text-text-primary">endast en gång</span> direkt efter att
          den skapats — kopiera och lagra den säkert. Tappar du bort nyckeln måste du återkalla
          den och skapa en ny.
        </p>

        <form
          onSubmit={createApiKey}
          className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end max-w-2xl"
        >
          <div className="flex-1">
            <label className="text-xs text-text-muted" htmlFor="api-key-label-input">
              Etikett (beskriver var nyckeln används)
            </label>
            <input
              id="api-key-label-input"
              data-testid="api-key-label-input"
              type="text"
              maxLength={80}
              value={newKeyLabel}
              onChange={(e) => setNewKeyLabel(e.target.value)}
              className="tk-input w-full"
              placeholder="t.ex. kaplist-track produktion"
            />
          </div>
          <button
            type="submit"
            data-testid="create-api-key-button"
            disabled={creatingKey || !newKeyLabel.trim()}
            className="tk-btn-primary"
          >
            {creatingKey ? "Skapar…" : "Skapa API-nyckel"}
          </button>
        </form>

        {revealedKey && (
          <div
            data-testid="revealed-api-key-banner"
            className="mt-5 rounded-lg border border-warning/40 bg-warning/10 p-4"
          >
            <div className="text-xs uppercase tracking-wider text-warning font-semibold">
              Kopiera nyckeln nu — den visas inte igen
            </div>
            <div className="mt-1 text-sm text-text-muted">
              Nyckel för <span className="font-medium text-text-primary">{revealedKey.label}</span>:
            </div>
            <div className="mt-3 flex items-center gap-2">
              <code
                data-testid="revealed-api-key-value"
                className="flex-1 font-mono text-xs sm:text-sm bg-surface-hover px-3 py-2 rounded border border-app break-all select-all"
              >
                {revealedKey.plaintext_key}
              </code>
              <button
                type="button"
                data-testid="copy-api-key-button"
                onClick={copyPlaintextKey}
                className="tk-btn-outline inline-flex items-center gap-2 shrink-0"
              >
                {copiedKey ? <Check size={16} /> : <Copy size={16} />}
                {copiedKey ? "Kopierad" : "Kopiera"}
              </button>
            </div>
            <button
              type="button"
              data-testid="dismiss-revealed-key-button"
              onClick={() => setRevealedKey(null)}
              className="mt-3 text-xs text-text-muted hover:text-text-primary underline"
            >
              Jag har sparat nyckeln — dölj
            </button>
          </div>
        )}

        <div className="mt-6 divide-y divide-[var(--border-c)]" data-testid="api-keys-list">
          {apiKeysLoading ? (
            <p className="text-sm text-text-muted py-4">Hämtar nycklar…</p>
          ) : apiKeys.length === 0 ? (
            <p className="text-sm text-text-muted py-4" data-testid="api-keys-empty">
              Inga API-nycklar skapade ännu.
            </p>
          ) : (
            apiKeys.map((k) => (
              <div
                key={k.id}
                data-testid={`api-key-row-${k.id}`}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="font-heading text-sm truncate">{k.label}</div>
                  <div className="text-xs text-text-muted font-mono mt-0.5">
                    {k.prefix}… ·{" "}
                    {k.is_active ? (
                      <span className="text-success">Aktiv</span>
                    ) : (
                      <span className="text-danger">Återkallad</span>
                    )}
                  </div>
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Skapad: {formatApiKeyDate(k.created_at)}
                    {k.created_by_name ? ` av ${k.created_by_name}` : ""}
                    {k.last_used_at
                      ? ` · Senast använd: ${formatApiKeyDate(k.last_used_at)}`
                      : " · Aldrig använd"}
                  </div>
                </div>
                {k.is_active && (
                  <button
                    type="button"
                    data-testid={`revoke-api-key-${k.id}`}
                    onClick={() => revokeApiKey(k)}
                    className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 shrink-0"
                  >
                    <Trash size={14} /> Återkalla
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      <section className="tk-card p-6" data-testid="integrations-docs-section">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
          <Plugs size={14} /> Dokumentation
        </div>
        <p className="text-sm text-text-muted mt-2">
          Fullständig integrationsdokumentation för <span className="font-medium text-text-primary">kaplist-track</span>{" "}
          (endpoints, autentisering, exempel) finns i filen{" "}
          <code className="font-mono text-xs bg-surface-hover px-1.5 py-0.5 rounded">
            INTEGRATION_KAPLIST_TRACK.md
          </code>{" "}
          i projektets rotmapp.
        </p>
      </section>
    </div>
  );
}

function formatApiKeyDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "—";
  }
}
